import { useEffect, useState } from 'react'
import type { MouseEvent, ReactNode } from 'react'
import { Login } from './pages/Login.tsx'
import { TopBar } from './components/TopBar.tsx'
import { UserMenu, UserMenuGroup, UserMenuItem } from './components/UserMenu.tsx'
import { icons } from './components/icons.ts'
import { Queue } from './pages/Queue.tsx'
import { VerificationDetail } from './pages/VerificationDetail.tsx'
import { Synonyms } from './pages/Synonyms.tsx'
import { Product } from './pages/Product.tsx'
import { GuidedReview } from './pages/GuidedReview.tsx'
import { Simulations } from './pages/Simulations.tsx'
import { Simulation } from './pages/Simulation.tsx'
import { GoldenSet } from './pages/GoldenSet.tsx'
import { Branches } from './pages/Branches.tsx'
import { Branch } from './pages/Branch.tsx'
import { Accounts } from './pages/Accounts.tsx'
import { Account } from './pages/Account.tsx'
import { Forms } from './pages/Forms.tsx'
import { Form } from './pages/Form.tsx'
import { FormEquivalences } from './pages/FormEquivalences.tsx'
import { Users } from './pages/Users.tsx'
import { User } from './pages/User.tsx'
import { InviteUser } from './pages/InviteUser.tsx'
import { Roles } from './pages/Roles.tsx'
import { Role } from './pages/Role.tsx'
import { GoldenCase } from './pages/GoldenCase.tsx'
import { ResolutionSet } from './pages/ResolutionSet.tsx'
import { Jobs } from './pages/Jobs.tsx'
import { Status } from './pages/Status.tsx'
import { Reports } from './pages/Reports.tsx'
import { AiManagement } from './pages/AiManagement.tsx'
import { Products } from './pages/Products.tsx'
import { ResolutionCase } from './pages/ResolutionCase.tsx'
import { FeedbackQueue } from './pages/FeedbackQueue.tsx'
import { FeedbackReview } from './pages/FeedbackReview.tsx'
import { ensureFeedbackAccount as ensureFeedbackAccountSession } from './lib/feedbackAccount.ts'
import {
  api,
  SESSION_EXPIRED_EVENT,
  storeToken,
  storedToken,
  type SessionAccount,
} from './lib/api.ts'
import { sessionEmail } from './lib/session.ts'
import {
  afterSave,
  needsPositions,
  positionsFor,
  STAGE_POSITIONS_SAVED_EVENT,
  type StagePositionsRead,
} from './lib/sessionStagePositions.ts'
import { navigate, REPORT_TABS, toPath, useRoute, type Route } from './lib/route.ts'
import { listedFindingRoute } from './lib/findingsList.ts'
import type { VerificationAt } from './lib/route.ts'
import { useVerificationId } from './lib/verificationAt.ts'

/**
 * Four screens and a top bar, addressed by URL.
 *
 * The route is the status: an verification is `/verifications/:id`, so it survives a reload,
 * answers the back button, and can be pasted to somebody else. It used to live
 * in `useState`, which meant an verification being analysed had nowhere to point at
 * while it ran -- see `lib/route.ts`.
 */
/** Routes that fill the window instead of flowing down it. */
const FILLS_VIEWPORT: ReadonlySet<string> = new Set([
  'simulation',
  'verification',
  'queue',
  'goldenCase',
  'feedbackReview',
])

/**
 * A verification screen, given either form of its address.
 *
 * `/6/833127` has to become a uuid before anything inside a conferência can
 * run, and this is where that happens -- in front of the screens rather than
 * inside them, so `GuidedReview` and `VerificationDetail` still take an id and
 * know nothing about how they were addressed.
 *
 * A uuid address resolves with no request at all, so the only cost falls on the
 * form somebody typed or was sent.
 */
function AtVerification({
  at,
  children,
}: {
  at: VerificationAt
  children: (id: string) => ReactNode
}) {
  const resolved = useVerificationId(at)

  if (resolved.state === 'loading') {
    return <p className="text-tSmall text-text-weak p-6">Abrindo a conferência…</p>
  }
  if (resolved.state === 'missing') {
    /*
     * The two numbers are typed and shared by people, so a wrong one is a
     * normal event rather than a broken link. Say which numbers were tried:
     * the usual mistake is the branch, and a pharmacist can see that at once.
     */
    return (
      <div className="flex flex-col gap-2 p-6">
        <p className="text-tP text-text-strong">Nenhuma conferência com esses números.</p>
        {at.kind === 'numbered' && (
          <p className="text-tSmall text-text-weak">
            Filial {at.branch}, orçamento {at.quotation}.
          </p>
        )}
        <button
          type="button"
          className="text-tSmall text-text-brand self-start underline"
          onClick={() => navigate({ name: 'queue' })}
        >
          Voltar para a fila
        </button>
      </div>
    )
  }

  return children(resolved.id)
}

export function App() {
  const [signedIn, setSignedIn] = useState(storedToken() !== undefined)
  // Seeded from the token, not only from sign-in: a reload used to leave this
  // empty, which made the user menu render nothing and took the only way to
  // log out with it.
  const [email, setEmail] = useState(sessionEmail() ?? '')
  const route = useRoute()
  // Undefined until asked, false when the route 404s. A pharmacy's admin
  // never learns the surface exists, because the nav entry never renders.
  const [canSimulate, setCanSimulate] = useState(false)
  // Driven from the header search. The queue owns its own filter box; this is
  // the second way in, and V1 has both.
  /*
   * The accounts this session may open, and the one it is in.
   *
   * Only rendered when there is more than one: a pharmacist with a single
   * account has no choice to make, and a picker offering one option is a
   * control that does nothing.
   */
  const [accounts, setAccounts] = useState<SessionAccount[]>([])
  const [accountId, setAccountId] = useState<string | undefined>()
  /*
   * Whether this session operates the system, or uses it.
   *
   * Read from the session rather than inferred from whether some screen
   * happens to answer. `canSimulate` was that inference and it works, but it
   * says "the simulation route did not 404", which is a different sentence from
   * "this person is a super admin" and stops being true the moment a route
   * moves.
   */
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  // Whoever's role may manage users runs the users and roles screens (#280,
  // ADR 0024); nothing else in the menu is a pharmacy's.
  const [canManageUsers, setCanManageUsers] = useState(false)
  // The stages this person's role reaches, undefined until the session says.
  const [stages, setStages] = useState<string[] | undefined>()
  // The statuses this person's role reaches (#439), undefined until the session says.
  const [statuses, setStatuses] = useState<string[] | undefined>()
  // Where this account's stages start on its FC12540 (#673), with the account
  // they were read for: see `sessionStagePositions.ts`.
  const [stagePositionsRead, setStagePositionsRead] = useState<StagePositionsRead | undefined>()
  const stagePositions = positionsFor(stagePositionsRead, accountId)

  /*
   * An expired session is a navigation, not an error message.
   *
   * It used to surface as "a sessão expirou" printed over the queue -- a
   * sentence about the app's plumbing where things wrong with an verification go, and
   * nothing a person can act on. The token is already cleared by then; this is
   * what turns that into the login page.
   */
  useEffect(() => {
    const onExpired = () => {
      setSignedIn(false)
      navigate({ name: 'queue' })
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
  }, [])

  useEffect(() => {
    if (!signedIn) return
    void api.simulations().then((runs) => setCanSimulate(runs !== undefined))
    void api
      .session()
      .then((s) => {
        setAccounts(s.accounts)
        setAccountId(s.accountId)
        setIsSuperAdmin(s.role === 'super_admin')
        setCanManageUsers(s.canManageUsers)
        setStages(s.stages)
        setStatuses(s.statuses)
        setStagePositionsRead({ accountId: s.accountId, positions: s.stagePositions })
      })
      // A picker that cannot load is a picker that does not render. It is not
      // worth an error banner over the queue somebody came here to read.
      .catch(() => undefined)
  }, [signedIn])

  // The account moved in place: read where its stages start.
  const positionsReadFor = stagePositionsRead?.accountId
  useEffect(() => {
    if (!needsPositions(signedIn, accountId, positionsReadFor)) return
    void api
      .session()
      .then((s) => setStagePositionsRead({ accountId: s.accountId, positions: s.stagePositions }))
      .catch(() => undefined)
  }, [signedIn, accountId, positionsReadFor])

  // Its positions saved on its page: draw with the new ones from now on.
  useEffect(() => {
    const onSaved = (event: Event) => {
      const saved = (event as CustomEvent<StagePositionsRead>).detail
      setStagePositionsRead((read) => afterSave(read, saved, accountId))
    }
    window.addEventListener(STAGE_POSITIONS_SAVED_EVENT, onSaved)
    return () => window.removeEventListener(STAGE_POSITIONS_SAVED_EVENT, onSaved)
  }, [accountId])

  /*
   * A switch reloads the app rather than re-fetching in place.
   *
   * Every screen is scoped to the session's account -- the queue, the filters,
   * the products, the counts -- so a switch invalidates all of it at once.
   * Reloading is honest about that; re-fetching piecemeal leaves whichever
   * screen forgot to refresh showing another pharmacy's rows.
   */
  async function switchAccount(next: string) {
    try {
      const { token } = await api.switchAccount(next)
      storeToken(token)
      window.location.reload()
    } catch {
      // Refused, which the server decides. Nothing to do but stay put.
    }
  }

  /*
   * Screens that own the viewport rather than flowing down it.
   *
   * Most of the app is a document: it grows, and the window scrolls. A
   * workspace is not -- a page beside a panel, with navigation pinned top and
   * bottom -- and there the window scrolling is the bug, because it carries
   * the buttons off screen just as somebody reaches for them.
   *
   * `min-h-dvh` cannot express this. It means *at least* the viewport, so
   * anything overflowing grows the document instead of scrolling inside it.
   * The height has to be exact and the overflow refused here, at the only
   * element that owns the whole window.
   */
  const fillsViewport = FILLS_VIEWPORT.has(route.name)
  const feedbackWorkspace = route.name === 'feedbackReview'
  const ensureFeedbackAccount = async (target: string, isCurrent: () => boolean): Promise<void> => {
    await ensureFeedbackAccountSession(
      accountId,
      target,
      isCurrent,
      api.switchAccount,
      (token, next) => {
        storeToken(token)
        setAccountId(next)
      },
    )
  }

  /**
   * Follows a link to a conferência that may be another pharmacy's.
   *
   * The link opens its own tab; only another pharmacy's needs help. The detail
   * is scoped to the session's account, a super admin's included, so it opens
   * only after switching to that account. The tab is opened now, inside the
   * click, because a browser blocks one opened after the switch has answered.
   */
  const openInAccount = (target: string, to: Route, event: MouseEvent<HTMLAnchorElement>) => {
    if (target === accountId) return
    event.preventDefault()
    const path = toPath(to)
    const tab = window.open('about:blank', '_blank')
    void api
      .switchAccount(target)
      .then(({ token }) => {
        storeToken(token)
        // This tab stays where it is, now under that account.
        setAccountId(target)
      })
      .catch(() => undefined)
      .then(() => {
        if (tab === null) window.location.assign(path)
        else tab.location.replace(path)
      })
  }
  const feedbackReviewActions = {
    visibleStatuses: statuses,
    stagePositions,
    onOpenInAccount: (
      target: string,
      verificationId: string,
      event: MouseEvent<HTMLAnchorElement>,
    ) =>
      openInAccount(
        target,
        { name: 'verification', at: { kind: 'identified', id: verificationId } },
        event,
      ),
    onOpenCase: (caseId: string) => navigate({ name: 'goldenCase', caseId }),
    onOpenResolutionCase: (caseId: string) => navigate({ name: 'resolutionCase', caseId }),
  }

  return (
    /*
     * V1's shell: everything -- header and content -- inside one centred
     * 1440px column (1140 was too narrow on a wide monitor), so the mark's
     * left edge lines up with the table's.
     */
    <div className={`flex flex-col px-6 ${fillsViewport ? 'h-dvh overflow-hidden' : 'min-h-dvh'}`}>
      <div
        className={`mx-auto flex w-full ${feedbackWorkspace ? '' : 'max-w-[1440px]'} min-h-0 flex-1 flex-col gap-5 ${
          // A workspace cannot spend 2.5rem of a laptop screen on breathing
          // room below the fold it does not have; a document can.
          fillsViewport ? 'pb-4' : 'pb-10'
        }`}
      >
        {/*
          The lockup is chrome and shows on every screen, signed in or not --
          V1 renders it on the login page too, and arriving at a bare form
          under no branding reads like the wrong address. Only the actions are
          gated: nothing on the right until there is a session to act on.
        */}
        <TopBar onHome={() => navigate({ name: 'queue' })}>
          {signedIn && (
            <UserMenu name={email}>
              {/*
                The account being viewed, above everything it scopes.

                Super admins only, and by role rather than by how many accounts
                somebody happens to belong to: the section is what tells you
                which pharmacy the screens below are showing, and a person who
                cannot switch has no use for that question.
              */}
              {canSimulate && accounts.length > 0 && (
                <>
                  <UserMenuGroup label="Conta" />
                  {accounts.map((account) => (
                    <UserMenuItem
                      key={account.id}
                      indented
                      onClick={() => {
                        void switchAccount(account.id)
                      }}
                    >
                      {/* The current one is marked rather than hidden: a list
                          that silently omits where you are reads as a bug. */}
                      {account.id === accountId ? `✓ ${account.name}` : account.name}
                    </UserMenuItem>
                  ))}
                </>
              )}

              {(canManageUsers || canSimulate) && (
                <>
                  {/*
                    Operacional is where whoever manages a pharmacy's users
                    finds them and its roles too. The rest of the group, and all of
                    Ferramentas, is super-admin: every entry there reaches a
                    pharmacy's database, every customer at once, or a screen
                    that decides what a pharmacist is asked.
                  */}
                  <UserMenuGroup label="Operacional" />
                  {canManageUsers && (
                    <>
                      <UserMenuItem indented onClick={() => navigate({ name: 'users' })}>
                        Usuários
                      </UserMenuItem>
                      <UserMenuItem indented onClick={() => navigate({ name: 'roles' })}>
                        Funções
                      </UserMenuItem>
                    </>
                  )}
                  {canSimulate && (
                    <>
                      <UserMenuItem indented onClick={() => navigate({ name: 'accounts' })}>
                        Contas
                      </UserMenuItem>
                      <UserMenuItem indented onClick={() => navigate({ name: 'forms' })}>
                        Formas farmacêuticas
                      </UserMenuItem>
                      <UserMenuItem indented onClick={() => navigate({ name: 'branches' })}>
                        Filiais
                      </UserMenuItem>
                      <UserMenuItem indented onClick={() => navigate({ name: 'products' })}>
                        Produtos
                      </UserMenuItem>
                    </>
                  )}
                </>
              )}

              {canSimulate && (
                <>
                  <UserMenuGroup label="Ferramentas" />
                  <UserMenuItem indented onClick={() => navigate({ name: 'simulations' })}>
                    Simulação
                  </UserMenuItem>
                  <UserMenuItem indented onClick={() => navigate({ name: 'goldenSet' })}>
                    Golden Set
                  </UserMenuItem>
                  <UserMenuItem indented onClick={() => navigate({ name: 'jobs' })}>
                    Rotinas
                  </UserMenuItem>
                  <UserMenuItem indented onClick={() => navigate({ name: 'status' })}>
                    Status
                  </UserMenuItem>
                  <UserMenuItem indented onClick={() => navigate({ name: 'ai' })}>
                    Gestão de IA
                  </UserMenuItem>
                  <UserMenuItem
                    indented
                    onClick={() => navigate({ name: 'reports', tab: REPORT_TABS[0] })}
                  >
                    Relatórios
                  </UserMenuItem>
                  <UserMenuItem indented onClick={() => navigate({ name: 'feedback' })}>
                    Acompanhamento de feedbacks
                  </UserMenuItem>
                  <div className="border-stroke-weak mt-1 border-t" />
                </>
              )}
              <UserMenuItem
                tone="danger"
                icon={icons.logout}
                onClick={() => {
                  storeToken(undefined)
                  setSignedIn(false)
                  navigate({ name: 'queue' })
                }}
              >
                Sair
              </UserMenuItem>
            </UserMenu>
          )}
        </TopBar>
        {/*
          Grows into whatever the header leaves, and `min-h-0` lets a child
          that wants the full height actually get it -- a flex item defaults to
          min-height:auto, which refuses to shrink below its content and makes
          any inner overflow scroll the whole page instead.

          Screens that do not opt in are unchanged: they size to their content
          and the document scrolls as before.
        */}
        <main className="flex min-h-0 flex-1 flex-col">
          {!signedIn ? (
            <Login
              onSignedIn={(who) => {
                setEmail(who)
                setSignedIn(true)
              }}
            />
          ) : route.name === 'simulations' ? (
            <Simulations
              onBack={() => navigate({ name: 'queue' })}
              onOpenVerification={(id) =>
                navigate({ name: 'verification', at: { kind: 'identified', id } })
              }
              onOpenSimulation={(uploadId) => navigate({ name: 'simulation', uploadId })}
            />
          ) : route.name === 'users' && canManageUsers ? (
            <Users
              onBack={() => navigate({ name: 'queue' })}
              onOpen={(id) => navigate({ name: 'user', id })}
              onInvite={() => navigate({ name: 'userInvite' })}
            />
          ) : route.name === 'user' && canManageUsers ? (
            <User key={route.id} id={route.id} onBack={() => navigate({ name: 'users' })} />
          ) : route.name === 'userInvite' && canManageUsers ? (
            <InviteUser onBack={() => navigate({ name: 'users' })} />
          ) : route.name === 'roles' && canManageUsers ? (
            <Roles
              canCreate={isSuperAdmin}
              onBack={() => navigate({ name: 'queue' })}
              onOpen={(id) => navigate({ name: 'role', id })}
              onCreate={() => navigate({ name: 'role' })}
            />
          ) : route.name === 'role' && canManageUsers ? (
            <Role
              key={route.id ?? 'new'}
              id={route.id}
              canManage={isSuperAdmin}
              onBack={() => navigate({ name: 'roles' })}
              onCreated={(id) => navigate({ name: 'role', id })}
            />
          ) : route.name === 'goldenSet' ? (
            <GoldenSet
              onBack={() => navigate({ name: 'queue' })}
              onOpenCase={(caseId) => navigate({ name: 'goldenCase', caseId })}
            />
          ) : route.name === 'jobs' ? (
            <Jobs onBack={() => navigate({ name: 'queue' })} />
          ) : route.name === 'branches' ? (
            <Branches
              onBack={() => navigate({ name: 'queue' })}
              onOpen={(id) => navigate({ name: 'branch', id })}
            />
          ) : route.name === 'branch' ? (
            <Branch id={route.id} onBack={() => navigate({ name: 'branches' })} />
          ) : route.name === 'accounts' ? (
            <Accounts
              onBack={() => navigate({ name: 'queue' })}
              onOpen={(id) => navigate({ name: 'account', id, tab: 'details' })}
            />
          ) : route.name === 'account' ? (
            <Account id={route.id} tab={route.tab} onBack={() => navigate({ name: 'accounts' })} />
          ) : route.name === 'forms' && isSuperAdmin ? (
            <Forms
              accountId={accountId}
              onOpenForm={(id) => navigate({ name: 'form', id })}
              onBack={() => navigate({ name: 'queue' })}
            />
          ) : route.name === 'formEquivalences' && isSuperAdmin ? (
            <FormEquivalences accountId={accountId} onBack={() => navigate({ name: 'queue' })} />
          ) : route.name === 'form' && isSuperAdmin ? (
            <Form
              accountId={accountId}
              form={route.id}
              onBack={() => navigate({ name: 'forms' })}
            />
          ) : route.name === 'status' ? (
            <Status onBack={() => navigate({ name: 'queue' })} />
          ) : route.name === 'ai' ? (
            <AiManagement onBack={() => navigate({ name: 'queue' })} />
          ) : route.name === 'reports' && isSuperAdmin ? (
            <Reports
              tab={route.tab}
              sessionAccountId={accountId}
              onBack={() => navigate({ name: 'queue' })}
              onOpenVerification={(item, event) =>
                openInAccount(item.accountId, listedFindingRoute(item), event)
              }
              onOpenAttentionVerification={(item, event) =>
                openInAccount(
                  item.accountId,
                  { name: 'verification', at: { kind: 'identified', id: item.id } },
                  event,
                )
              }
            />
          ) : route.name === 'feedback' && isSuperAdmin ? (
            <FeedbackQueue
              initialFilters={route.filters}
              initialPage={route.page}
              onBack={() => navigate({ name: 'queue' })}
              onOpenVerification={(item, filters, page, event) => {
                if (
                  event.button !== 0 ||
                  event.ctrlKey ||
                  event.metaKey ||
                  event.shiftKey ||
                  event.altKey
                )
                  return
                event.preventDefault()
                navigate({ name: 'feedbackReview', eventId: item.id, filters, queuePage: page })
              }}
            />
          ) : route.name === 'feedbackReview' && isSuperAdmin ? (
            <FeedbackReview
              key={JSON.stringify(route.filters ?? {})}
              eventId={route.eventId}
              filters={route.filters}
              horizon={route.horizon}
              reviewActions={feedbackReviewActions}
              onEnsureAccount={ensureFeedbackAccount}
              onHorizon={(horizon) => navigate({ ...route, horizon }, { replace: true })}
              onOpen={(eventId, horizon) =>
                navigate({ ...route, eventId, horizon }, { replace: true })
              }
              onExit={() =>
                navigate({ name: 'feedback', filters: route.filters, page: route.queuePage })
              }
            />
          ) : route.name === 'resolutionSet' ? (
            <ResolutionSet
              onBack={() => navigate({ name: 'queue' })}
              onOpenCase={(caseId) => navigate({ name: 'resolutionCase', caseId })}
            />
          ) : route.name === 'resolutionCase' ? (
            <ResolutionCase
              caseId={route.caseId}
              onBack={() => navigate({ name: 'resolutionSet' })}
            />
          ) : route.name === 'goldenCase' ? (
            <GoldenCase
              caseId={route.caseId}
              onBack={() => navigate({ name: 'goldenSet' })}
              onOpenCase={(caseId) => navigate({ name: 'goldenCase', caseId })}
              onSimulate={(uploadId) => navigate({ name: 'simulation', uploadId })}
              onOpenVerification={(id) =>
                navigate({ name: 'verification', at: { kind: 'identified', id } })
              }
            />
          ) : route.name === 'simulation' ? (
            <Simulation
              uploadId={route.uploadId}
              onBack={() => navigate({ name: 'simulations' })}
              onOpenCase={(caseId) => navigate({ name: 'goldenCase', caseId })}
              onOpenVerification={(id) =>
                navigate({ name: 'verification', at: { kind: 'identified', id } })
              }
            />
          ) : route.name === 'products' && isSuperAdmin ? (
            <Products
              onBack={() => navigate({ name: 'queue' })}
              onOpenProduct={(erpCode) => navigate({ name: 'product', erpCode })}
            />
          ) : route.name === 'product' && isSuperAdmin ? (
            <Product erpCode={route.erpCode} accountId={accountId} onBack={() => history.back()} />
          ) : route.name === 'synonyms' ? (
            <Synonyms onBack={() => navigate({ name: 'queue' })} />
          ) : route.name === 'verification' ? (
            /* Opening an verification opens the pass that walks you through it. */
            <AtVerification at={route.at}>
              {(id) => (
                <GuidedReview
                  // A new verification is a new screen. Without the key React keeps
                  // the instance across the hand-off from one conferência to the
                  // next, and the second opened on the first's last step.
                  key={id}
                  id={id}
                  isSuperAdmin={isSuperAdmin}
                  onOpenInAccount={(target, verificationId, event) =>
                    openInAccount(
                      target,
                      { name: 'verification', at: { kind: 'identified', id: verificationId } },
                      event,
                    )
                  }
                  visibleStatuses={statuses}
                  stagePositions={stagePositions}
                  focusFinding={route.finding}
                  focusNear={route.near}
                  onExit={() => navigate({ name: 'queue' })}
                  onNext={(next) =>
                    navigate({ name: 'verification', at: { kind: 'identified', id: next } })
                  }
                  onOpenCase={(caseId) => navigate({ name: 'goldenCase', caseId })}
                  onOpenResolutionCase={(caseId) => navigate({ name: 'resolutionCase', caseId })}
                />
              )}
            </AtVerification>
          ) : route.name === 'full' ? (
            <AtVerification at={route.at}>
              {(id) => (
                <VerificationDetail
                  id={id}
                  onBack={() => navigate({ name: 'queue' })}
                  onOpenCase={(caseId) => navigate({ name: 'goldenCase', caseId })}
                />
              )}
            </AtVerification>
          ) : (
            <Queue
              visibleStages={stages}
              visibleStatuses={statuses}
              stagePositions={stagePositions}
              onOpen={(row) =>
                navigate({
                  name: 'verification',
                  /*
                   * A simulation keeps the uuid address.
                   *
                   * It has no branch and no quotation: it is written with
                   * branch 0 and a negative quotation number, counting down,
                   * so that a dry run can never collide with a real ERP key.
                   * `/0/-1` is not something anybody says, reads out or types,
                   * which is the whole reason the numbered form exists.
                   */
                  at: row.simulation
                    ? { kind: 'identified', id: row.id }
                    : { kind: 'numbered', branch: row.branch, quotation: row.quotation },
                })
              }
            />
          )}
        </main>
      </div>
    </div>
  )
}
